import { Drawer } from "antd";
import { SlidersHorizontal } from "lucide-react";

import useQuestionFilters, {
  useQuestionFilterApplyEffect,
} from "../../../core/hooks/useQuestionFilters";
import ActiveFilters from "./ActiveFilters";
import FilterPanel from "./FilterPanel";
import useCompactLayout from "./useCompactLayout";
import { FOCUS_RING, cx } from "./tokens";

export interface FilterChipStripProps {
  /** Extra classes on the outer strip — callers own their own spacing/margins. */
  className?: string;
}

/**
 * The "Filtros" entry point, the active-filter chips, and the drawer they open.
 *
 * Shared by `FilterBar` (the global app-shell strip) and `/home`'s own page
 * header strip (`home-page-header-filter-chips`), which replaces the global
 * one on that one route (`DefaultLayout hideFilter`). Both read the same
 * store and must each mount `useQuestionFilterApplyEffect` exactly once —
 * since the two never render on the same route at the same time, mounting it
 * here keeps that true without either caller tracking it.
 */
export default function FilterChipStrip({ className }: FilterChipStripProps) {
  const { activeFilters, selectedCount, panelOpen, setPanelOpen, remove, clearAll } =
    useQuestionFilters();

  useQuestionFilterApplyEffect();

  const compact = useCompactLayout();

  return (
    <>
      <div
        className={cx(
          "rounded-card border border-edge bg-surface px-3 py-3 shadow-panel sm:px-4",
          className,
        )}
      >
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setPanelOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={panelOpen}
            className={cx(
              "inline-flex h-10 shrink-0 items-center gap-2 rounded-input border border-edge bg-surface px-4",
              "text-sm font-medium text-ink transition-colors hover:bg-surface-hover",
              FOCUS_RING,
            )}
          >
            <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
            Filtros
            {selectedCount > 0 ? (
              <span
                className="rounded-full bg-select/[0.14] px-2 py-0.5 text-[11px] font-semibold tabular-nums text-select-ink"
                aria-label={`${selectedCount} filtros selecionados`}
              >
                {selectedCount}
              </span>
            ) : null}
          </button>

          {activeFilters.length > 0 ? (
            <ActiveFilters
              variant="bar"
              items={activeFilters}
              onRemove={(item) => remove(item.dimension, item.value)}
              onClearAll={clearAll}
              className="min-w-0 flex-1"
            />
          ) : (
            <p className="truncate text-[13px] text-ink-muted">
              Nenhum filtro aplicado — todas as questões do seu banco.
            </p>
          )}
        </div>
      </div>

      <Drawer
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        // Below lg the panel is a full-height sheet, which is also the width at
        // which the panel drops its rail and turns sections into accordions.
        placement={compact ? "bottom" : "right"}
        width={compact ? undefined : "min(1560px, 98vw)"}
        height={compact ? "94dvh" : undefined}
        title="Filtros"
        rootClassName="acertei-filter-drawer"
        styles={{ body: { padding: 0 } }}
        destroyOnClose={false}
      >
        <FilterPanel showHeading />
      </Drawer>
    </>
  );
}
